/**
 * Per-page SEO tags for the technology and university pages, from the
 * server's `page.seo`: document.title, meta description, robots, canonical,
 * Open Graph, Twitter card and JSON-LD.
 *
 * Same approach as ipintel/seo.ts, extended: the app is one index.html, so
 * the tags are written into <head> while the page is mounted and put back
 * exactly as they were afterwards (a tag we created is removed, a tag that
 * already existed gets its old value back), so the next route never inherits
 * this page's canonical or robots. The title is not restored — the next page
 * sets its own, and restoring would flash the old one during a route change.
 *
 * Crawlers that do not run scripts get the same tags from the server-rendered
 * shell (/api/v1/seo/render); this is for everything that does.
 */

import { useEffect } from 'react';
import type { PageSeo } from './api';

type Undo = () => void;

function upsert(selector: string, create: () => HTMLElement, attr: string, value: string): Undo {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    el.setAttribute(attr, value);
    document.head.appendChild(el);
    const created = el;
    return () => { created.remove(); };
  }
  const had = el.hasAttribute(attr);
  const old = el.getAttribute(attr);
  el.setAttribute(attr, value);
  const existing = el;
  return () => {
    if (had && old !== null) existing.setAttribute(attr, old);
    else existing.removeAttribute(attr);
  };
}

const metaName = (name: string, content: string): Undo =>
  upsert(`meta[name="${name}"]`, () => { const m = document.createElement('meta'); m.setAttribute('name', name); return m; }, 'content', content);

const metaProp = (prop: string, content: string): Undo =>
  upsert(`meta[property="${prop}"]`, () => { const m = document.createElement('meta'); m.setAttribute('property', prop); return m; }, 'content', content);

const linkRel = (rel: string, href: string): Undo =>
  upsert(`link[rel="${rel}"]`, () => { const l = document.createElement('link'); l.setAttribute('rel', rel); return l; }, 'href', href);

/** JSON-LD blocks, one <script> each; removed on undo. `<` is escaped so a
 *  title can never close the script element. */
const jsonLd = (items: any[]): Undo => {
  const els = items.filter(Boolean).map((item, i) => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-techpage-jsonld', String(i));
    s.text = JSON.stringify(item).replace(/</g, '\\u003c');
    document.head.appendChild(s);
    return s;
  });
  return () => els.forEach(e => e.remove());
};

/** Writes `seo` into <head> while mounted (and whenever it changes). */
export function usePageSeo(seo: PageSeo | null | undefined): void {
  const key = seo ? JSON.stringify(seo) : '';
  useEffect(() => {
    if (!seo) return;
    if (seo.title) document.title = seo.title;
    const undo: Undo[] = [];
    try {
      const og = seo.og || {};
      const title = og.title || seo.title;
      const desc = og.description || seo.description;
      const url = og.url || seo.canonical;
      if (seo.description) undo.push(metaName('description', seo.description));
      if (seo.robots) undo.push(metaName('robots', seo.robots));
      if (seo.canonical) undo.push(linkRel('canonical', seo.canonical));
      if (title) undo.push(metaProp('og:title', title));
      if (desc) undo.push(metaProp('og:description', desc));
      if (url) undo.push(metaProp('og:url', url));
      undo.push(metaProp('og:type', og.type || 'website'));
      undo.push(metaProp('og:site_name', 'UniversityTech.io'));
      if (og.image) undo.push(metaProp('og:image', og.image));
      undo.push(metaName('twitter:card', og.image ? 'summary_large_image' : 'summary'));
      if (title) undo.push(metaName('twitter:title', title));
      if (desc) undo.push(metaName('twitter:description', desc));
      if (og.image) undo.push(metaName('twitter:image', og.image));
      if (Array.isArray(seo.json_ld) && seo.json_ld.length) undo.push(jsonLd(seo.json_ld));
    } catch { /* a head we cannot write is not worth breaking the page */ }
    return () => { undo.reverse().forEach(u => { try { u(); } catch { /* ignore */ } }); };
    // `key` captures every field of seo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/** robots noindex while mounted — for the SPA's not-found and error views,
 *  which the server still answers with 200. */
export function useNoIndex(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    let undo: Undo | null = null;
    try { undo = metaName('robots', 'noindex'); } catch { /* ignore */ }
    return () => { try { undo?.(); } catch { /* ignore */ } };
  }, [active]);
}
